import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, race, timer } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import { Api } from '../../../../shared/constants/api';
import { FeatureFlagName } from '../../../../shared/interfaces/feature-flag.interface';
import { SharedCommonUtility } from '../../../../shared/utils/common.utility';
import { ActivatedRouteData } from '../../shared/constants';
import { FeatureManagementService } from './feature-management.service';

/** How long a route waits for the FME SDK before it evaluates the flag with whatever treatment it has. */
export const FME_FEATURE_FLAG_GUARD_TIMEOUT_MS: number = 5000;

/**
 * Allows the route only when every FME flag in `route.data.fmeFeatureFlag` (a name or a list of names) is on.
 * Otherwise navigates to the forbidden page. `FeatureFlagGuard` only handles Harness flags.
 */
export const FmeFeatureFlagGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
): Observable<boolean | UrlTree> | UrlTree | boolean => {
  const featureManagementService: FeatureManagementService = inject(FeatureManagementService);
  const router: Router = inject(Router);
  const forbiddenPage: UrlTree = router.parseUrl(Api.forbidden);
  const flags: FeatureFlagName[] = [route.data[ActivatedRouteData.fmeFeatureFlag] ?? []].flat();

  if (SharedCommonUtility.isNullishOrEmpty(flags)) {
    console.warn('[FmeFeatureFlagGuard.canActivate] An FME feature flag name must be provided.');
    return forbiddenPage;
  }

  const evaluate = (): boolean | UrlTree =>
    flags.every((flag: FeatureFlagName): boolean => featureManagementService.isOn(flag)()) ? true : forbiddenPage;

  if (featureManagementService.ready()) {
    return evaluate();
  }

  // The SDK is still loading flag definitions, so a flag would read as its fallback treatment. Wait for it.
  return race(toObservable(featureManagementService.ready).pipe(filter(Boolean)), timer(FME_FEATURE_FLAG_GUARD_TIMEOUT_MS)).pipe(
    take(1),
    map(evaluate),
  );
};
